import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Plane, ChevronDown, Search } from 'lucide-react'
import { clsx } from 'clsx'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { useSearch } from '../hooks/useSearch'
import SearchResults from './SearchResults'

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { 
    name: 'Services', 
    href: '#',
    dropdown: [
      { name: 'Flights', href: '/flights' },
      { name: 'Day Trips', href: 'https://www.viator.com/en-ZA/Bali/d98-ttd?pid=P00165007&uid=U00478366&mcid=58086&currency=ZAR', external: true },
      { name: 'Travel Insurance', href: '/services#travel-insurance' },
      { name: 'Shuttle Services', href: '/services#shuttle-services' },
      { name: 'View All Services', href: '/services' }
    ]
  },
  { name: 'Packages', href: '/packages' },
  { name: 'Reviews', href: '/reviews' },
  { name: 'Contact', href: '/contact' },
]

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false)
  const location = useLocation()
  const { franchiseData, loading } = useFranchiseData()
  const {
    searchQuery,
    searchResults,
    isSearchOpen,
    setIsSearchOpen,
    handleSearch,
    handleResultClick,
    clearSearch
  } = useSearch()

  const searchRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Handle clicks outside search
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [setIsSearchOpen])

  // Handle keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && isSearchOpen) {
        clearSearch()
      }
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        setIsSearchOpen(true)
        setTimeout(() => searchInputRef.current?.focus(), 100)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isSearchOpen, clearSearch, setIsSearchOpen])

  const isHome = location.pathname === '/'
  const hasPageHeader = ['/about', '/packages', '/contact', '/services'].includes(location.pathname)
  const headerClass = clsx(
    'absolute top-0 left-0 right-0 z-50',
    isHome || hasPageHeader ? 'bg-transparent shadow-lg' : 'bg-[#006D77]'
  )

  // Show loading state or fallback
  if (loading || !franchiseData) {
    return (
      <header className={headerClass}>
        <nav className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8">
          <div className="flex lg:flex-1">
            <div className="animate-pulse flex items-center space-x-2">
              <div className="h-8 w-8 bg-white rounded"></div>
              <div className="h-6 w-32 bg-white rounded"></div>
            </div>
          </div>
        </nav>
      </header>
    )
  }

  return (
    <header className={headerClass}>
      <nav className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8" aria-label="Global">
        {/* Left side - Social Media Icons */}
        <div className="flex items-center space-x-4">
          {/* WhatsApp */}
          <a href="https://api.whatsapp.com/send?phone=27693688545" target="_blank" rel="noopener noreferrer" className="text-white hover:text-green-400 transition-colors">
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
            </svg>
          </a>
          {/* Instagram */}
          <a href="https://www.instagram.com/mementotravelco" target="_blank" rel="noopener noreferrer" className="text-white hover:text-pink-400 transition-colors">
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12.017 0C8.396 0 7.989.016 6.756.072 5.526.129 4.697.316 3.969.633c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.313 4.867.126 5.696.072 6.927.016 8.16 0 8.567 0 12.017s.016 3.857.072 5.09c.057 1.231.244 2.06.561 2.787.306.789.717 1.459 1.384 2.126s1.337 1.078 2.126 1.384c.727.317 1.556.504 2.787.561 1.233.056 1.64.072 5.09.072s3.857-.016 5.09-.072c1.231-.057 2.06-.244 2.787-.561.789-.306 1.459-.717 2.126-1.384s1.078-1.337 1.384-2.126c.317-.727.504-1.556.561-2.787.056-1.233.072-1.64.072-5.09s-.016-3.857-.072-5.09c-.057-1.231-.244-2.06-.561-2.787-.306-.789-.717-1.459-1.384-2.126S19.65.935 18.86.63c-.727-.317-1.556-.504-2.787-.561C14.84.016 14.433 0 12.017 0zM12.017 2.162c3.204 0 3.584.012 4.849.07 1.17.054 1.805.249 2.227.415.56.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.058 1.265.07 1.645.07 4.849s-.012 3.584-.07 4.849c-.054 1.17-.249 1.805-.413 2.227-.217.56-.477.96-.896 1.382-.419.419-.819.679-1.381.896-.422.164-1.057.36-2.227.413-1.265.058-1.645.07-4.849.07s-3.584-.012-4.849-.07c-1.17-.054-1.805-.249-2.227-.413-.56-.217-.96-.477-1.382-.896-.419-.419-.679-.819-.896-1.381-.164-.422-.36-1.057-.413-2.227-.058-1.265-.07-1.645-.07-4.849s.012-3.584.07-4.849c.054-1.17.249-1.805.413-2.227.217-.56.477-.96.896-1.382.419-.419.819-.679 1.381-.896.422-.164 1.057-.36 2.227-.413 1.265-.058 1.645-.07 4.849-.07z"/>
              <path d="M12.017 5.838a6.179 6.179 0 1 0 0 12.358 6.179 6.179 0 0 0 0-12.358zM12.017 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"/>
              <path d="M19.846 5.595a1.444 1.444 0 1 1-2.889 0 1.444 1.444 0 0 1 2.889 0z"/>
            </svg>
          </a>
          {/* Facebook */}
          <a href="https://www.facebook.com/mementotravelco" target="_blank" rel="noopener noreferrer" className="text-white hover:text-blue-400 transition-colors">
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a>
        </div>

        {/* Mobile menu button */}
        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-white"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Right side - Navigation + Search */}
        <div className="hidden lg:flex items-center space-x-12">
          {navigation.map((item) => (
            <div key={item.name} className="relative">
              {item.dropdown ? (
                <div className="relative">
                  <button
                    onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                    className={clsx(
                      'text-sm font-light leading-6 text-white transition-all duration-300 hover:text-[#83C5BE] hover:underline flex items-center gap-1',
                      location.pathname === item.href
                        ? 'text-blue-400'
                        : 'text-white'
                    )}
                  >
                    {item.name}
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  {servicesDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-2 z-50">
                      {item.dropdown.map((dropdownItem) => (
                        dropdownItem.external ? (
                          <a
                            key={dropdownItem.name}
                            href={dropdownItem.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-4 py-2 text-sm transition-colors font-light text-gray-700 hover:bg-gray-100"
                            onClick={() => setServicesDropdownOpen(false)}
                          >
                            {dropdownItem.name}
                          </a>
                        ) : (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.href}
                            className={`block px-4 py-2 text-sm transition-colors font-light ${
                              dropdownItem.name === 'View All Services'
                                ? 'text-white hover:opacity-90'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            style={dropdownItem.name === 'View All Services' ? {
                              background: '#006D77'
                            } : {}}
                            onClick={() => setServicesDropdownOpen(false)}
                          >
                            {dropdownItem.name}
                          </Link>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                item.name === 'Reviews' ? (
                  <a
                    href="/#reviews"
                    className={clsx(
                      'text-sm font-light leading-6 text-white transition-all duration-300 hover:text-[#83C5BE] hover:underline cursor-pointer'
                    )}
                  >
                    {item.name}
                  </a>
                ) : (
            <Link
              to={item.href}
              className={clsx(
                      'text-sm font-light leading-6 text-white transition-all duration-300 hover:text-[#83C5BE] hover:underline',
                location.pathname === item.href
                        ? 'text-blue-400'
                        : 'text-white'
              )}
            >
              {item.name}
            </Link>
                )
              )}
            </div>
          ))}
          
          {/* Search */}
          <div className="relative" ref={searchRef}>
            {isSearchOpen ? (
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchResults.length > 0) {
                        handleResultClick(searchResults[0])
                      }
                    }}
                    className="w-64 px-4 py-2 text-sm text-gray-900 bg-white rounded-md shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400 font-light pr-10"
                    placeholder="Search destinations, services, packages..."
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  <SearchResults
                    results={searchResults}
                    query={searchQuery}
                    onResultClick={handleResultClick}
                    isVisible={isSearchOpen && (searchQuery.trim().length > 0 || searchResults.length > 0)}
                  />
                </div>
                <button
                  onClick={() => setIsSearchOpen(false)}
                  className="text-white hover:text-[#83C5BE] transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsSearchOpen(true)}
                className="text-white hover:text-[#83C5BE] transition-colors p-2 relative"
                title="Search (Ctrl+K)"
              >
                <Search className="h-6 w-6" />
              </button>
            )}
        </div>
        </div>
      </nav>
      
      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden" role="dialog" aria-modal="true">
          <div className="fixed inset-0 z-50"></div>
          <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
            <div className="flex items-center justify-between">
              <Link to="/" className="-m-1.5 p-1.5 flex items-center space-x-4">
                {/* WhatsApp */}
                <a href="https://api.whatsapp.com/send?phone=27693688545" target="_blank" rel="noopener noreferrer" className="hover:text-green-400 transition-colors" style={{color: '#006D77'}}>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                </a>
                {/* Instagram */}
                <a href="https://www.instagram.com/mementotravelco" target="_blank" rel="noopener noreferrer" className="hover:text-pink-400 transition-colors" style={{color: '#006D77'}}>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C8.396 0 7.989.016 6.756.072 5.526.129 4.697.316 3.969.633c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.313 4.867.126 5.696.072 6.927.016 8.16 0 8.567 0 12.017s.016 3.857.072 5.09c.057 1.231.244 2.06.561 2.787.306.789.717 1.459 1.384 2.126s1.337 1.078 2.126 1.384c.727.317 1.556.504 2.787.561 1.233.056 1.64.072 5.09.072s3.857-.016 5.09-.072c1.231-.057 2.06-.244 2.787-.561.789-.306 1.459-.717 2.126-1.384s1.078-1.337 1.384-2.126c.317-.727.504-1.556.561-2.787.056-1.233.072-1.64.072-5.09s-.016-3.857-.072-5.09c-.057-1.231-.244-2.06-.561-2.787-.306-.789-.717-1.459-1.384-2.126S19.65.935 18.86.63c-.727-.317-1.556-.504-2.787-.561C14.84.016 14.433 0 12.017 0zM12.017 2.162c3.204 0 3.584.012 4.849.07 1.17.054 1.805.249 2.227.415.56.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.058 1.265.07 1.645.07 4.849s-.012 3.584-.07 4.849c-.054 1.17-.249 1.805-.413 2.227-.217.56-.477.96-.896 1.382-.419.419-.819.679-1.381.896-.422.164-1.057.36-2.227.413-1.265.058-1.645.07-4.849.07s-3.584-.012-4.849-.07c-1.17-.054-1.805-.249-2.227-.413-.56-.217-.96-.477-1.382-.896-.419-.419-.679-.819-.896-1.381-.164-.422-.36-1.057-.413-2.227-.058-1.265-.07-1.645-.07-4.849s.012-3.584.07-4.849c.054-1.17.249-1.805.413-2.227.217-.56.477-.96.896-1.382.419-.419.819-.679 1.381-.896.422-.164 1.057-.36 2.227-.413 1.265-.058 1.645-.07 4.849-.07z"/>
                    <path d="M12.017 5.838a6.179 6.179 0 1 0 0 12.358 6.179 6.179 0 0 0 0-12.358zM12.017 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"/>
                    <path d="M19.846 5.595a1.444 1.444 0 1 1-2.889 0 1.444 1.444 0 0 1 2.889 0z"/>
                  </svg>
                </a>
                {/* Facebook */}
                <a href="https://www.facebook.com/mementotravelco" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors" style={{color: '#006D77'}}>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              </Link>
              <button
                type="button"
                className="-m-2.5 rounded-md p-2.5 text-gray-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-2 py-6">
                  {navigation.map((item) => (
                    <div key={item.name}>
                      {item.dropdown ? (
                        <div>
                          <button
                            onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                            className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:text-[#006D77] transition w-full text-left flex items-center justify-between"
                          >
                            {item.name}
                            <ChevronDown className={clsx("h-4 w-4 transition-transform", servicesDropdownOpen && "rotate-180")} />
                          </button>
                          {servicesDropdownOpen && (
                            <div className="ml-4 mt-2 space-y-1">
                              {item.dropdown.map((dropdownItem) => (
                                dropdownItem.external ? (
                                  <a
                                    key={dropdownItem.name}
                                    href={dropdownItem.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block px-3 py-2 text-sm text-gray-700 hover:text-[#006D77] rounded-lg transition-colors"
                                    onClick={() => {
                                      setServicesDropdownOpen(false)
                                      setMobileMenuOpen(false)
                                    }}
                                  >
                                    {dropdownItem.name}
                                  </a>
                                ) : (
                                  <Link
                                    key={dropdownItem.name}
                                    to={dropdownItem.href}
                                    className="block px-3 py-2 text-sm text-gray-700 hover:text-[#006D77] rounded-lg transition-colors"
                                    onClick={() => {
                                      setServicesDropdownOpen(false)
                                      setMobileMenuOpen(false)
                                    }}
                                  >
                                    {dropdownItem.name}
                                  </Link>
                                )
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        item.name === 'Reviews' ? (
                          <a
                            href="/#reviews"
                            className={clsx(
                              '-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:text-[#006D77] transition cursor-pointer'
                            )}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {item.name}
                          </a>
                        ) : (
                    <Link
                      to={item.href}
                      className={clsx(
                              '-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 hover:text-[#006D77] transition',
                        location.pathname === item.href
                                ? 'text-white'
                          : 'text-gray-900'
                      )}
                            style={location.pathname === item.href ? {background: '#006D77'} : {}}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                        )
                      )}
                    </div>
                  ))}
                </div>
                {/* Mobile Search */}
                <div className="py-6 border-b border-gray-500/10">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && searchResults.length > 0) {
                          handleResultClick(searchResults[0])
                          setMobileMenuOpen(false)
                        }
                      }}
                      className="w-full px-4 py-3 text-sm text-gray-900 bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 font-light pr-10"
                      placeholder="Search destinations, services, packages..."
                    />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    {searchQuery && (
                      <button
                        onClick={clearSearch}
                        className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {searchQuery.trim() && (
                    <div className="mt-4 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
                      {searchResults.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                          <p className="text-sm">No results found for "{searchQuery}"</p>
                        </div>
                      ) : (
                        <div className="py-2">
                          {searchResults.slice(0, 5).map((result) => (
                            <button
                              key={result.id}
                              onClick={() => {
                                handleResultClick(result)
                                setMobileMenuOpen(false)
                              }}
                              className="w-full px-4 py-3 hover:bg-gray-50 text-left transition-colors duration-150"
                            >
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {result.title}
                              </h4>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                {result.content.length > 80
                                  ? `${result.content.substring(0, 80)}...`
                                  : result.content
                                }
                              </p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="py-6">
                  <a href="#contact" className="btn text-white w-full text-center text-base font-semibold px-6 py-2 rounded-md shadow hover:opacity-90 transition" style={{background: '#006D77'}}>
                    JOIN US
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  )
} 